'use client';

import { ChangeEvent, useEffect } from 'react';
import MapPinIcon from '../Icons/MapPin';
import { cn } from '@/lib/utils';
import useLocalStorage from '@/hooks/useLocalStorage';

type Props = {
  onSwitch: (mapVisibility: boolean) => void;
  isLoading?: boolean;
};

type VillageFormat = 'with-map' | 'without-map';

export default function VillagesHeaderSwitch({
  onSwitch,
  isLoading = false,
}: Props) {
  const [villagesFormatValue, setVillagesFormat] = useLocalStorage<
    VillageFormat | []
  >('villages-format', 'without-map');

  const villagesFormat =
    villagesFormatValue instanceof Array ? 'without-map' : villagesFormatValue;

  useEffect(() => {
    const showMap = villagesFormat === 'with-map';
    onSwitch(showMap);
  }, [onSwitch, villagesFormat]);

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    window.scrollTo({
      top: 400,
      behavior: 'smooth',
    });
    setVillagesFormat(event.target.value as VillageFormat);
  }

  return (
    <div className="hidden desktop:flex items-end w-[240px] ml-auto mt-2">
      <div
        className={cn(
          'flex w-fit justify-between mt-2 ml-auto border border-blue',
          {
            'opacity-40': isLoading,
          },
        )}>
        <label
          className={cn(
            'cursor-pointer w-11 h-[44px] bg-white text-blue flex items-center justify-center transition ease-in-out duration-[600ms]',
            {
              'bg-blue text-white': villagesFormat === 'without-map',
              'cursor-not-allowed': isLoading,
              'hover:bg-blue hover:text-white': !isLoading,
            },
          )}
          htmlFor="without-map">
          <svg
            className="w-7 h-[30px] fill-current"
            viewBox="0 0 23 23"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M3 11h8V3H3m0 18h8v-8H3m10 8h8v-8h-8m0-10v8h8V3" />
          </svg>
        </label>
        <input
          onChange={handleOnChange}
          checked={villagesFormat === 'without-map'}
          value="without-map"
          id="without-map"
          type="radio"
          className="hidden"
        />
        <label
          className={cn(
            'cursor-pointer w-11 h-[44px] bg-white text-blue flex items-center justify-center transition ease-in-out duration-[600ms]',
            {
              'bg-blue text-white': villagesFormat === 'with-map',
              'cursor-not-allowed': isLoading,
              'hover:bg-blue hover:text-white': !isLoading,
            },
          )}
          htmlFor="with-map">
          <MapPinIcon className="w-6 h-6 fill-current" />
        </label>
        <input
          onChange={handleOnChange}
          checked={villagesFormat === 'with-map'}
          value="with-map"
          id="with-map"
          type="radio"
          className="hidden"
        />
      </div>
    </div>
  );
}
